import { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Badge from "@mui/material/Badge";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

import Breadcrumbs from "parts/Breadcrumbs";
import NotificationItem from "parts/Items/NotificationItem";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "./styles";

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  LanguageContext,
} from "context";

import NotificationsIcon from "@mui/icons-material/Notifications";
import { useSessionContext } from "context/SessionContext";
import { useMessageNotification } from "context/MessageNotificationContext";

import routes from "routes";

import { logoutApi } from "@shared/api/auth";

interface Props {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}

const findRouteKeysFromPath = (pathname: string): string[] => {
  const searchPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  
  let matchedKeys: string[] = [];

  const traverse = (routeList: any[], breadcrumbTrail: string[]): boolean => {
    for (const r of routeList) {
      // divider や title はパンくずに含めない
      const currentTrail = r.type !== "divider" && r.type !== "title" 
        ? [...breadcrumbTrail, r.translationKey || r.name] 
        : breadcrumbTrail;

      if (r.route === searchPath) {
        matchedKeys = currentTrail;
        return true;
      }
      if (r.collapse) {
        if (traverse(r.collapse, currentTrail)) {
          return true;
        }
      }
    }
    return false;
  };

  traverse(routes, []);

  // マッチしなかった場合は URL のパスを分割して返す（フォールバック）
  if (matchedKeys.length === 0) {
    const path = searchPath.replace(/^\/|\/$/g, "");
    return path.split("/");
  }

  return matchedKeys;
};

function DashboardNavbar({ absolute, light, isMini }: Props): JSX.Element {
  const [navbarType, setNavbarType] = useState<
    "fixed" | "absolute" | "relative" | "static" | "sticky"
  >();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState<null | HTMLElement>(null);

  const location = useLocation();
  const resolvedRouteKeys = findRouteKeysFromPath(location.pathname);
  const navigate = useNavigate();

  const { logout, refresh, accessToken } = useSessionContext();
  
  // as any を使用し、安全に各種プロパティを取得
  const { language, setLanguage, t } = useContext(LanguageContext) as any;

  const { messages, unreadCount, markAsRead } = useMessageNotification();
  const audioRef = useRef<HTMLAudioElement>(null);
  const prevCountRef = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      audioRef.current?.play().catch(() => {});
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(null);

  const handleLogout = async () => {
    await logoutApi(accessToken);

    await refresh();
    await logout();
    navigate("/pages/auth/SignIn");
  };

  const renderMenu = () => {
    const unreadMessages = messages.filter((m) => !m.read);

    return (
      <Menu
        anchorEl={openMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(openMenu)}
        onClose={handleCloseMenu}
        sx={{ mt: 2 }}
      >
        {unreadMessages.length === 0 ? (
          <MenuItem onClick={handleCloseMenu}>{t("DashboardNavbar.empty") || "通知はありません"}</MenuItem>
        ) : (
          unreadMessages.map((m) => (
            <MenuItem
              key={m.id}
              onClick={() => {
                markAsRead(m.id);
                handleCloseMenu();
              }}
              sx={{
                backgroundColor: m.read ? "white" : "#fff8e1",
                whiteSpace: "normal",
                alignItems: "flex-start",
                paddingY: 1,
              }}
            >
              <NotificationItem
                icon={<CircleNotificationsIcon fontSize="small" />}
                title={m.title}
                content={m.content}
                dateTime={m.datetime}
              />
            </MenuItem>
          ))
        )}

        <MenuItem
          onClick={() => {
            navigate("/pages/MessageNotification/MessageCenter");
            handleCloseMenu();
          }}
        >
          {t("DashboardNavbar.open") || "通知センターを開く"}
        </MenuItem>
      </Menu>
    );
  };

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }: any) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  // パンくずのタイトル（現在のページ名）を翻訳
  const currentTitleRaw = resolvedRouteKeys.length > 0 ? resolvedRouteKeys[resolvedRouteKeys.length - 1] : "";
  const currentTitleTranslated = t(currentTitleRaw) || currentTitleRaw;

  // パンくずの経路を翻訳
  const breadcrumbRoutes = resolvedRouteKeys.map(key => t(key) || key);

  return (
    <>
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar
          sx={[
            navbarContainer,
            {
              flexWrap: { xs: "wrap", md: "nowrap" },
              paddingTop: { xs: 1, md: 0 },
              paddingBottom: { xs: 1, md: 0 },
            },
          ]}
        >
          {/* 左側: パンくず & メニューアイコン */}
          <MDBox
            color="inherit"
            mb={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: { xs: "100%", md: "auto" },
              justifyContent: "space-between",
            }}
          >
            <Breadcrumbs
              icon="home"
              title={currentTitleTranslated}
              route={breadcrumbRoutes}
              light={light}
            />
            <IconButton
              sx={navbarDesktopMenu}
              onClick={handleMiniSidenav}
              size="small"
              disableRipple
            >
              <Icon fontSize="medium" sx={iconsStyle}>
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
          </MDBox>

          {/* 右側: 検索、言語、アイコン群 */}
          {!isMini && (
            <MDBox
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "flex-end",
                alignItems: { xs: "flex-end", md: "center" },
                width: { xs: "100%", md: "auto" },
                flex: 1,
                gap: { xs: 2, md: 2 },
                mt: { xs: 2, md: 0 },
              }}
            >
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: { xs: "100%", md: "auto" },
                  gap: 1,
                }}
              >
                <MDBox sx={{ flex: { xs: 1, md: "none" } }}>
                  <MDInput label={t("DefaultNavbar.Search") || "Search"} sx={{ width: "100%" }} />
                </MDBox>

                <FormControl sx={{ minWidth: { xs: 100, sm: 120 } }}>
                  <InputLabel id="lang-select-small-label" size="normal">
                    {t("DefaultNavbar.Language") || "Language"}
                  </InputLabel>
                  <Select
                    label={t("DefaultNavbar.Language") || "Language"}
                    id="ChooseLanguage"
                    autoWidth
                    margin="dense"
                    size="medium"
                    value={language || "ja"} 
                    style={{ width: "100%", height: 48 }}
                    onChange={(e: any) => setLanguage?.(e.target.value)}
                  >
                    <MenuItem value="en">{t("DefaultNavbar.English") || "English"}</MenuItem>
                    <MenuItem value="ja">{t("DefaultNavbar.Japanese") || "日本語"}</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>

              <MDBox
                color={light ? "white" : "inherit"}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flexWrap: "nowrap",
                  gap: { xs: 0.5, sm: 1 },
                  pr: { xs: 0, sm: 2, md: 3 },
                }}
              >
                <IconButton
                  sx={navbarIconButton}
                  size="small"
                  disableRipple
                  onClick={() => navigate("/pages/account/AccountSettings")}
                >
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>

                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon sx={iconsStyle} fontSize="medium">
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>

                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleConfiguratorOpen}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>

                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={() => window.open("https://chronospark.jp/help/", "_blank", "noopener,noreferrer")}
                >
                  <Icon sx={iconsStyle}>help_outline</Icon>
                </IconButton>

                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleOpenMenu}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon sx={iconsStyle} />
                  </Badge>
                </IconButton>

                {renderMenu()}

                <IconButton sx={navbarIconButton} size="small" disableRipple onClick={handleLogout}>
                  <Icon sx={iconsStyle}>logout</Icon>
                </IconButton>
              </MDBox>
            </MDBox>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

export default DashboardNavbar;